import type { TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { useDeleteTeam } from "./use-team";

interface DeleteTeamDialogProps {
  team: TeamOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the team was deleted (the edit dialog closes with it). */
  onDeleted: () => void;
}

/** Deleting a team is permanent: ask first, say what happens to the chats and agents using it. */
export function DeleteTeamDialog({ team, open, onOpenChange, onDeleted }: DeleteTeamDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const remove = useDeleteTeam();
  const confirm = () => {
    remove.mutate(team.id, {
      onSuccess: () => {
        toast.success(t("team.teams.deleted", { name: team.name }));
        onOpenChange(false);
        onDeleted();
      },
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <DialogContent
        title={t("team.teams.confirmDeleteTitle", { name: team.name })}
        description={t("team.teams.confirmDeleteBody")}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("team.cancel")}
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={confirm}>
              <Icon name="trash" />
              {t("team.teams.confirmDelete")}
            </Button>
          </>
        }
      >
        {remove.isError ? <FormAlert title={message(remove.error)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
